import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { formatPrice } from "@/utils/currency";
import { getLocaleSettings, getLocaleCode } from "@/utils/locale";
import { useCartPrefill } from "@/hooks/useCartPrefill";
import { toast } from "sonner";

export const SOURCES = [
  { key: "phone", label: "Teléfono", color: "bg-blue-500" },
  { key: "whatsapp", label: "WhatsApp", color: "bg-green-500" },
  { key: "counter", label: "Balcão", color: "bg-orange-500" },
  { key: "platform", label: "Plataforma", color: "bg-purple-500" },
];

export const PLATFORMS = [
  { key: "uber_eats", label: "Uber Eats" },
  { key: "bolt_food", label: "Bolt Food" },
  { key: "ifood", label: "iFood" },
  { key: "glovo", label: "Glovo" },
  { key: "just_eat", label: "Just Eat" },
  { key: "other", label: "Otra plataforma" },
];

export const PHONE_PATTERNS = {
  ES: { prefix: "+34", flag: "\uD83C\uDDEA\uD83C\uDDF8", digits: 9, example: "612 345 678", mask: "XXX XXX XXX" },
  PT: { prefix: "+351", flag: "\uD83C\uDDF5\uD83C\uDDF9", digits: 9, example: "912 345 678", mask: "XXX XXX XXX" },
  BR: { prefix: "+55", flag: "\uD83C\uDDE7\uD83C\uDDF7", digits: 11, example: "11 91234 5678", mask: "XX XXXXX XXXX" },
};

export const CARD_BRANDS = [
  { key: "visa_debit", label: "Visa Débito", color: "bg-blue-700" },
  { key: "visa_credit", label: "Visa Crédito", color: "bg-blue-600" },
  { key: "master_debit", label: "Master Débito", color: "bg-red-600" },
  { key: "master_credit", label: "Master Crédito", color: "bg-red-500" },
  { key: "amex", label: "Amex", color: "bg-indigo-600" },
  { key: "cabal", label: "Cabal", color: "bg-teal-600" },
];

function haversineKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function calcShipping(zones, distKm, defaultFee, freeKm = 0, subtotal = 0) {
  if (freeKm > 0 && distKm <= freeKm) return 0;
  const active = zones.filter(z => z.active !== false).sort((a, b) => a.max_km - b.max_km);
  if (!active.length) return defaultFee;
  const zone = active.find(z => distKm <= z.max_km) || active[active.length - 1];
  return zone.free_above != null && subtotal >= Number(zone.free_above) ? 0 : Number(zone.fee ?? defaultFee);
}

export function useNewOrder() {
  const { authAxios, loading: authLoading } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const locale = getLocaleSettings();
  const localeCode = getLocaleCode();

  // Data
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tables, setTables] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);

  // Restaurant delivery config
  const [deliveryZones, setDeliveryZones] = useState([]);
  const [restaurantLat, setRestaurantLat] = useState(null);
  const [restaurantLng, setRestaurantLng] = useState(null);
  const [restaurantCity, setRestaurantCity] = useState("");
  const [restaurantCountry, setRestaurantCountry] = useState("");
  const [defaultShippingFee, setDefaultShippingFee] = useState(2.50);
  const [deliveryFreeKm, setDeliveryFreeKm] = useState(0);
  const [restaurantPaymentMethods, setRestaurantPaymentMethods] = useState([]);

  // Step tracking
  const [step, setStep] = useState(1);

  // Step 1
  const [source, setSource] = useState("");
  const [platform, setPlatform] = useState("");

  // Step 2
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [phoneValid, setPhoneValid] = useState(null);

  // Step 3
  const [deliveryType, setDeliveryType] = useState("");
  const [scheduleDate, setScheduleDate] = useState("asap");
  const [scheduleTime, setScheduleTime] = useState("");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [deliveryComplement, setDeliveryComplement] = useState("");
  const [deliveryBlock, setDeliveryBlock] = useState("");
  const [deliveryReference, setDeliveryReference] = useState("");
  const [deliveryNotes, setDeliveryNotes] = useState("");
  const [deliveryLat, setDeliveryLat] = useState(null);
  const [deliveryLng, setDeliveryLng] = useState(null);
  const [tableNumber, setTableNumber] = useState("");

  // Step 4
  const [cart, setCart] = useState([]);
  const [productSearch, setProductSearch] = useState("");
  const [showCategories, setShowCategories] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Prefill cart from duplicate order (via useCartPrefill hook)
  useCartPrefill(setCart);

  // Step 5
  const [discount, setDiscount] = useState("");
  const [discountType, setDiscountType] = useState("fixed"); // "fixed" | "percent"
  const [discountReason, setDiscountReason] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [observations, setObservations] = useState("");
  const [fiscalDoc, setFiscalDoc] = useState("");
  // Loyalty points
  const [foundCustomerPoints, setFoundCustomerPoints] = useState(0);
  const [pointsToRedeem, setPointsToRedeem] = useState(0);
  const POINTS_REDEMPTION_RATE = 100; // 100 points = 1 EUR

  // Step 6
  const [paymentType, setPaymentType] = useState("");
  const [cardBrand, setCardBrand] = useState("");
  const [cashReceived, setCashReceived] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [orderCreated, setOrderCreated] = useState(null);

  // Idempotency key — generated once per cart session, never inside createOrder().
  // Prevents duplicate orders from double-click, network retry, or browser lag.
  // A new key is generated after each successful submit so the next order starts fresh.
  const _idempotencyKeyRef = useRef(crypto.randomUUID());

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!authAxiosRef.current || authLoading) return;
    Promise.all([
      authAxiosRef.current.get("/menu/products").catch(() => ({ data: [] })),
      authAxiosRef.current.get("/menu/categories").catch(() => ({ data: [] })),
      authAxiosRef.current.get("/tables").catch(() => ({ data: [] })),
      authAxiosRef.current.get("/marketing/coupons").catch(() => ({ data: [] })),
      authAxiosRef.current.get("/restaurant").catch(() => ({ data: {} })),
    ]).then(([prodsRes, catsRes, tablesRes, couponsRes, restRes]) => {
      const allProds = Array.isArray(prodsRes.data) ? prodsRes.data : (prodsRes.data?.products || []);
      setProducts(allProds.filter(p => p.active !== false));
      setCategories(Array.isArray(catsRes.data) ? catsRes.data : (catsRes.data?.categories || []));
      const tablesData = Array.isArray(tablesRes.data) ? tablesRes.data : (tablesRes.data?.tables || tablesRes.data?.data || []);
      setTables(tablesData);
      setCoupons(Array.isArray(couponsRes.data) ? couponsRes.data : []);
      const rest = restRes.data || {};
      setDeliveryZones(rest.delivery_zones || []);
      setRestaurantLat(rest.lat ?? null);
      setRestaurantLng(rest.lng ?? null);
      setRestaurantCity(rest.city || "");
      setRestaurantCountry(rest.country || "");
      setDefaultShippingFee(rest.default_shipping_fee ?? 2.50);
      setDeliveryFreeKm(rest.delivery_free_km ?? 0);
      const rawMethods = rest.payment_methods || [];
      setRestaurantPaymentMethods(rawMethods.filter(m => m.active !== false));
      setDataLoading(false);
    }).catch(() => setDataLoading(false));
  }, [authLoading]);

  useEffect(() => {
    const pattern = PHONE_PATTERNS[localeCode];
    if (!pattern || !customerPhone) { setPhoneValid(null); return; }
    const digits = customerPhone.replace(/\D/g, "");
    setPhoneValid(digits.length === pattern.digits);
  }, [customerPhone, localeCode]);

  const getFullPhone = useCallback(() => {
    const pattern = PHONE_PATTERNS[localeCode];
    const digits = customerPhone.replace(/\D/g, "");
    return digits ? `${pattern?.prefix || "+34"}${digits}` : "";
  }, [customerPhone, localeCode]);

  const addToCart = useCallback((product) => {
    setCart(prev => {
      const existing = prev.find(c => c.product_id === product.id && !c.modifiers?.length);
      if (existing) return prev.map(c => c.product_id === product.id && !c.modifiers?.length ? { ...c, quantity: c.quantity + 1 } : c);
      return [...prev, { product_id: product.id, product_name: product.name, quantity: 1, unit_price: product.price, category: product.category_id }];
    });
    toast.success(`${product.name} agregado`);
  }, []);

  const addToCartWithModifiers = useCallback(({ product, modifiers, selected_variations, extra_price }) => {
    const unitPrice = (product.price || 0) + (extra_price || 0);
    const modifierLabel = (selected_variations || []).map(v =>
      (v.quantity || 1) > 1 ? `${v.quantity}× ${v.name}` : v.name
    ).join(", ") || modifiers.flatMap(g => g.options.map(o => o.name)).join(", ");
    setCart(prev => [...prev, {
      product_id: product.id,
      product_name: product.name + (modifierLabel ? ` (${modifierLabel})` : ""),
      quantity: 1,
      unit_price: unitPrice,
      category: product.category_id,
      modifiers,
      selected_variations: selected_variations || [],
    }]);
    toast.success(`${product.name} agregado`);
  }, []);

  const updateCartQty = useCallback((productId, delta) => {
    setCart(prev => prev.map(c =>
      (c.product_id === productId || c.cartId === productId || c.id === productId)
        ? { ...c, quantity: Math.max(0, c.quantity + delta) }
        : c
    ).filter(c => c.quantity > 0));
  }, []);

  const removeFromCart = useCallback((productId) => setCart(prev => prev.filter(c => c.product_id !== productId)), []);

  const subtotal = cart.reduce((sum, c) => sum + c.quantity * c.unit_price, 0);
  const discountRawValue = discount ? parseFloat(discount) || 0 : 0;
  const discountAmount = appliedCoupon
    ? (appliedCoupon.type === "percentage" ? subtotal * (appliedCoupon.value / 100) : appliedCoupon.value)
    : (discountType === "percent"
        ? Math.min(subtotal, subtotal * discountRawValue / 100)
        : Math.min(subtotal, discountRawValue));
  const pointsDiscount = pointsToRedeem > 0 ? Math.round(pointsToRedeem / POINTS_REDEMPTION_RATE * 100) / 100 : 0;
  const shipping = (() => {
    if (deliveryType !== "delivery") return 0;
    if (!deliveryAddress.trim()) return 0; // sem morada → sem taxa
    if (deliveryLat && deliveryLng && restaurantLat && restaurantLng) {
      const dist = haversineKm(restaurantLat, restaurantLng, deliveryLat, deliveryLng);
      return calcShipping(deliveryZones, dist, defaultShippingFee, deliveryFreeKm, subtotal);
    }
    // Morada introduzida mas sem coordenadas → aplica taxa padrão
    return defaultShippingFee;
  })();
  const total = Math.max(0, subtotal - discountAmount - pointsDiscount + shipping);

  const applyCoupon = useCallback(() => {
    const found = coupons.find(c => c.code?.toLowerCase() === couponCode.toLowerCase() && c.active);
    if (found) {
      setAppliedCoupon(found);
      toast.success(`Cupón "${found.code}" aplicado: ${found.type === "percentage" ? found.value + "%" : formatPrice(found.value)}`);
    } else {
      toast.error("Cupón inválido o expirado");
    }
  }, [coupons, couponCode]);

  const filteredProducts = products.filter(p => {
    if (selectedCategory && p.category_id !== selectedCategory) return false;
    if (deliveryType === "dineIn" && p.availability === "delivery_only") return false;
    if (deliveryType === "delivery" && p.availability === "table_only") return false;
    if (!productSearch) return true;
    const q = productSearch.toLowerCase();
    return p.name.toLowerCase().includes(q) || (p.description || "").toLowerCase().includes(q) || (p.code || "").includes(q);
  });

  const cashReceivedNum = parseFloat(cashReceived) || 0;
  const isCashBrand = cardBrand && /cash|efectivo|dinheiro|dinero/i.test(cardBrand);
  const changeAmount = isCashBrand && cashReceivedNum > 0
    ? Math.max(0, cashReceivedNum - total)
    : 0;

  const createOrder = async () => {
    if (!customerName.trim()) { toast.error("El nombre del cliente es obligatorio"); return; }
    if (deliveryType === "delivery" && !deliveryAddress.trim()) { toast.error("La dirección de entrega es obligatoria"); return; }
    if (cart.length === 0) { toast.error("Agrega al menos un producto"); return; }
    setSubmitting(true);
    try {
      const orderSource = source === "platform" ? platform : source === "counter" ? "counter" : source;
      const payMethod = paymentType === "card" ? cardBrand : paymentType;
      const scheduledFor = scheduleDate && scheduleDate !== "asap" && scheduleTime
        ? `${scheduleDate}T${scheduleTime}` : null;
      const res = await authAxios.post("/orders", {
        source: deliveryType === "dineIn" ? "table" : orderSource,
        table_number: deliveryType === "dineIn" ? parseInt(tableNumber) : null,
        customer_name: customerName,
        customer_phone: getFullPhone(),
        order_type: deliveryType === "dineIn" ? "dine_in" : deliveryType === "delivery" ? "delivery" : "pickup",
        delivery_address: deliveryType === "delivery" ? [deliveryAddress, deliveryComplement, deliveryBlock, deliveryReference].filter(Boolean).join(" — ") : "",
        delivery_lat: deliveryType === "delivery" ? deliveryLat : null,
        delivery_lng: deliveryType === "delivery" ? deliveryLng : null,
        notes: [observations, deliveryNotes ? `Entrega: ${deliveryNotes}` : ""].filter(Boolean).join(" | "),
        payment_method: payMethod,
        cash_received: payMethod === "cash" ? cashReceivedNum || null : null,
        change_amount: payMethod === "cash" ? changeAmount || null : null,
        coupon_code: appliedCoupon?.code || "",
        discount_amount: discountAmount + pointsDiscount,
        discount_type: appliedCoupon ? "fixed" : discountType,
        discount_value: appliedCoupon ? discountAmount : discountRawValue,
        discount_reason: discountReason || "",
        fiscal_document: fiscalDoc,
        scheduled_for: scheduledFor,
        items: cart.map(c => ({ product_id: c.product_id, product_name: c.product_name, quantity: c.quantity, unit_price: c.unit_price, notes: c.notes || "", modifiers: c.modifiers || [], selected_variations: c.selected_variations?.length > 0 ? c.selected_variations : null }))
      }, { headers: { "X-Idempotency-Key": _idempotencyKeyRef.current } });
      setOrderCreated(res.data);
      // Clear cart immediately after success so a second submit (any retry path)
      // finds an empty cart and the next "adicional" starts with no stale items.
      setCart([]);
      // Rotate key — next order session gets a fresh idempotency key.
      _idempotencyKeyRef.current = crypto.randomUUID();
      // Deduct loyalty points and send WA notification (non-blocking)
      if (pointsToRedeem > 0 && foundCustomerPoints >= pointsToRedeem) {
        authAxios.post("/loyalty/spend-points", {
          phone: getFullPhone(),
          points_to_spend: pointsToRedeem,
          order_id: res.data.id || res.data._id || ""
        }).catch(() => {});
      }
      toast.success("Pedido criado com sucesso!");
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail
        : Array.isArray(detail) ? detail.map(e => e?.msg || String(e)).join("; ")
        : "Error al crear pedido";
      toast.error(msg);
    }
    setSubmitting(false);
  };

  const resetOrder = () => {
    // Always rotate the idempotency key on manual reset so the next order
    // cannot share a key with any prior submit in this session.
    _idempotencyKeyRef.current = crypto.randomUUID();
    setOrderCreated(null); setStep(1); setCart([]); setSource(""); setPlatform("");
    setCustomerName(""); setCustomerPhone(""); setDeliveryType(""); setPaymentType("");
    setCardBrand(""); setCashReceived(""); setObservations(""); setDiscount(""); setDiscountType("fixed"); setDiscountReason(""); setCouponCode(""); setAppliedCoupon(null);
    setScheduleDate("asap"); setScheduleTime("");
    setFoundCustomerPoints(0); setPointsToRedeem(0);
    setDeliveryAddress(""); setDeliveryComplement(""); setDeliveryLat(null); setDeliveryLng(null); setTableNumber("");
  };

  const canProceedToStep2 = source && (source !== "platform" || platform);
  const canProceedToStep3 = canProceedToStep2 && customerName.trim() && customerPhone && (source !== "whatsapp" || phoneValid);
  const canProceedToStep4 = canProceedToStep3 && deliveryType && (deliveryType !== "delivery" || deliveryAddress.trim()) && (deliveryType !== "dineIn" || tableNumber);
  const canProceedToStep5 = canProceedToStep4 && cart.length > 0;

  return {
    locale, localeCode,
    products, categories, tables, coupons, dataLoading,
    cashReceived, setCashReceived, changeAmount, isCashBrand,
    step, setStep,
    source, setSource, platform, setPlatform,
    customerPhone, setCustomerPhone, customerName, setCustomerName, phoneValid,
    deliveryType, setDeliveryType, deliveryAddress, setDeliveryAddress,
    deliveryComplement, setDeliveryComplement,
    deliveryBlock, setDeliveryBlock,
    deliveryReference, setDeliveryReference,
    deliveryNotes, setDeliveryNotes,
    deliveryLat, setDeliveryLat, deliveryLng, setDeliveryLng,
    tableNumber, setTableNumber,
    cart, productSearch, setProductSearch, showCategories, setShowCategories,
    selectedCategory, setSelectedCategory, filteredProducts,
    discount, setDiscount, discountType, setDiscountType, discountReason, setDiscountReason,
    couponCode, setCouponCode, appliedCoupon, setAppliedCoupon,
    observations, setObservations, fiscalDoc, setFiscalDoc,
    foundCustomerPoints, setFoundCustomerPoints,
    pointsToRedeem, setPointsToRedeem, pointsDiscount,
    POINTS_REDEMPTION_RATE,
    paymentType, setPaymentType, cardBrand, setCardBrand,
    scheduleDate, setScheduleDate, scheduleTime, setScheduleTime,
    restaurantLat, restaurantLng, restaurantCity, restaurantCountry,
    restaurantPaymentMethods,
    submitting, orderCreated,
    subtotal, discountAmount, shipping, total,
    addToCart, addToCartWithModifiers, updateCartQty, updateQuantity: updateCartQty, removeFromCart, applyCoupon,
    createOrder, resetOrder,
    canProceedToStep2, canProceedToStep3, canProceedToStep4, canProceedToStep5,
  };
}
